/**
 * #1184 — when may a click on old account activity open Game Detail?
 *
 * Only when the page would actually receive the game: the live store resolves
 * the route to a row Game Detail renders sections for (`gameDetailReceives`).
 * A saved or retained pair alone mints no destination — a retired Kalshi
 * total's ticker still parses to a URL, and that URL opened a header plus an
 * empty consensus card (the orphan #1184 removes).
 *
 * The answer is point-in-time and store-only. "Not received" means the rows
 * loaded right now don't include the game — the feed may still be landing —
 * so it is never worded as retired/closed, never disables the href itself
 * (middle-click and new-tab keep it), and `useGameDestination` re-asks on the
 * shared throttled market tick. Nothing here issues a request or declares feed
 * demand.
 */
import { useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import { useThrottledMarketVersion } from '@/hooks/ui/useThrottledMarketVersion';
import { useMarketStore } from '@/stores/marketStore';
import { gameDetailReceives, type GameUrlParts } from '@/views/GameDetail/liveGameLookup';

// The three GameDetail route shapes (App.tsx). Most specific first.
const GAME_ROUTES = [
  '/game/:sport/:date/:teamA/:teamB/:gameNo',
  '/game/:sport/:date/:teamA/:teamB',
  '/game/:date/:teamA/:teamB',
] as const;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The GameDetail route params an href names, or null when it is not a Game
 *  Detail route (e.g. tennis' `/dashboard?focus=` deep link). A 4-segment path
 *  is the legacy `date/a/b` shape only when its first segment is a date —
 *  otherwise `:sport/:date/:a/:b` would be misread as a sport-less route. */
export function gameUrlPartsFromHref(href: string): GameUrlParts | null {
  const pathname = href.split(/[?#]/, 1)[0];
  for (const pattern of GAME_ROUTES) {
    const m = matchPath(pattern, pathname);
    if (!m) continue;
    const p: Partial<Record<'sport' | 'date' | 'teamA' | 'teamB' | 'gameNo', string>> = m.params;
    if (pattern === '/game/:date/:teamA/:teamB') {
      if (!DATE.test(p.date ?? '')) continue;
      return { date: p.date, teamA: p.teamA, teamB: p.teamB };
    }
    if (!DATE.test(p.date ?? '')) return null;
    return { sport: p.sport, date: p.date, teamA: p.teamA, teamB: p.teamB, gameNo: p.gameNo };
  }
  return null;
}

/**
 * - `live`: the game page resolves now. `lineInFeed` is false when the game is
 *   live but this exact ticker is not (a settled total in a game still being
 *   played) — the page shows the game, not the record's line.
 * - `absent`: no href, or a Game Detail href the loaded rows don't resolve.
 * - `unchecked`: an href that is not a Game Detail route (tennis' focused
 *   dashboard). Not this issue's orphan; callers keep their existing behavior.
 */
export type GameDestination =
  | { kind: 'live'; href: string; lineInFeed: boolean }
  | { kind: 'absent' }
  | { kind: 'unchecked'; href: string };

export function gameDestination(href: string | null, ticker: string): GameDestination {
  if (!href) return { kind: 'absent' };
  const parts = gameUrlPartsFromHref(href);
  if (!parts) return { kind: 'unchecked', href };
  if (!gameDetailReceives(parts)) return { kind: 'absent' };
  return { kind: 'live', href, lineInFeed: useMarketStore.getState().getMarket(ticker) != null };
}

/** `gameDestination`, re-asked on the shared 4 Hz market tick so a game that
 *  lands while a record or notice is open gains its link. One mounted record
 *  at a time; a legacy sport-less href scans every sport per tick, the same
 *  lookup Game Detail itself runs on that route. */
export function useGameDestination(href: string | null, ticker: string): GameDestination {
  const version = useThrottledMarketVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => gameDestination(href, ticker), [href, ticker, version]);
}

/**
 * The click-time check for an EXISTING link into Game Detail (My Trades label
 * links, Kalshi game chips): navigate exactly as before when the page receives
 * the game or the href isn't a Game Detail route; otherwise open the record.
 * Returns true when it handled the click (the caller then prevents the Link's
 * default). Modified clicks (new tab/window) are left to the browser.
 */
export function routeHistoricalClick(
  event: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean },
  href: string | null,
  ticker: string,
  openRecord: () => void,
): boolean {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (gameDestination(href, ticker).kind === 'absent') {
    openRecord();
    return true;
  }
  return false;
}
